import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { validateToken } from "../../api/info";
import { getWorkspaceInfo } from "../../api/workspace";
import Loader from "../../components/Loader";
import {
  setCalendarManagement,
  updateOpenAppointmentTypes,
} from "../../redux/actions/CalendarManagementActions";
import {
  setCompanyDirectory,
  updateContacts,
  updateContactsMode,
  updateOpenContacts,
  updateSyncDomain,
} from "../../redux/actions/CompanyDirectoryActions";
import {
  setExternalOverlay,
  setIds,
  setInfo,
  setNewWorkspace,
  setSaveError,
  setToken,
  updateChannelId,
  updateChannelIdError,
  updateDestinations,
} from "../../redux/actions/InfoActions";
import {
  setKnowledgeBase,
  updateKnowledgeBaseItems,
  updateOpenKnowledgeBaseItems,
} from "../../redux/actions/KnowledgeBaseActions";
import {
  createWorkspaceObject,
  getFeatureFlag,
  getOrganizationFeatureFlag,
  scrollToFirstErrorElement,
} from "../../services-logic/base-logic";
import { invalidCalendarManagementKeys } from "../../services-logic/calendar-management/calendar-management-logic";
import {
  COMPANY_DIRECTORY_KEY,
  invalidCompanyDirectoryKeys,
} from "../../services-logic/company-directory/company-directory-logic";
import {
  KNOWLEDGE_BASE_KEY,
  OPENING_HOURS_KEY,
  TEXT_KNOWLEDGE_ITEM_KEY,
  invalidKnowledgeBaseKeys,
} from "../../services-logic/knowledge-base/knowledge-base-logic";
import { debounce } from "../../utils/utilDebounceInput";
import {
  postClose,
  postHeight,
  postLoading,
  postOpenModal,
  postProgressData,
  postSubmitStatus,
} from "../../utils/utilPostMessage";
import { getServiceConfig } from "../../utils/utilServices";
import { getSaveButtonStatus, validateSave } from "../../validation/save-validation";
import CalendarManagement, { CALENDAR_MANAGEMENT } from "../calendar-management/CalendarManagement";
import CompanyDirectory from "../company-directory/CompanyDirectory";
import KnowledgeBase from "../knowledge-base/KnowledgeBase";
import ExitModalContainer from "../workspace-action-buttons/modals/exit-modal/ExitModalContainer";
import WorkspaceActionButtons from "../workspace-action-buttons/WorkspaceActionButtons";
import DisabledWorkspace from "../special-pages/DisabledWorkspace";
import GetStarted from "../special-pages/GetStarted";
import InvalidOrganization from "../special-pages/InvalidOrganization";
import PhoneNumberInUsedByOtherAccount from "../special-pages/PhoneNumberInUsedByOtherAccount";
import Unauthorized from "../special-pages/Unauthorized";
import YourReceptionist from "../your-receptionist/YourReceptionist";

import { putRequest } from "../../api/api";
import { formatPhoneNumberUsingFormats, validatePhoneNumber } from "../../utils/utilPhoneNumbers";
import SaveProgressModalContainer from "../workspace-action-buttons/modals/save-progress-modal/SaveProgressModalContainer";
import ChannelSection from "../channel-section/ChannelSection";
import store from "../../redux/store";

const Workspace = ({
  isLoading,
  setIsLoading,
  token,
  externalOverlay,
  organizationId,
  accountId,
  workspaceId,
  channelId = "",
  accountName,
  workspaceName,
  agentName,
  agentId,
  agentLanguage,
  showActionButtons = false,
  canEditChannel = true,
}) => {
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isValidOrganization, setIsValidOrganization] = useState(true);
  const [isPhoneNumberInUsed, setIsPhoneNumberInUsed] = useState(false);
  const [phoneIndexObject, setPhoneIndexObject] = useState(null);

  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [isSaveProgressModalOpen, setIsSaveProgressModalOpen] = useState(false);

  const { isValidToken, destinations } = useSelector((state) => state.info);
  const { isEnable } = useSelector((state) => state.info.workspace);
  const { updatedContacts } = useSelector((state) => state.companyDirectory);

  const [currentPercentage, setCurrentPercentage] = useState(0);
  const [updatePercentage, setUpdatePercentage] = useState(0);
  const [currentMessage, setCurrentMessage] = useState("");

  useEffect(() => {
    if (!isValidToken) {
      setIsAuthenticated(false);
    }
  }, [isValidToken]);

  useEffect(() => {
    dispatch(setExternalOverlay(externalOverlay));
    dispatch(setIds(organizationId, accountId, workspaceId));
    const initialData = async () => {
      postLoading(true);
      setIsLoading(true);

      var workspaceInfo = await getWorkspaceInfo(
        organizationId,
        accountId,
        workspaceId,
        token,
        channelId,
        accountName,
        workspaceName,
        agentName,
        agentId,
        agentLanguage
      );

      if (workspaceInfo === null || workspaceInfo.error) {
        if (workspaceInfo.code === "unauthorized") {
          setIsAuthenticated(false);
          postLoading(false);
          setIsLoading(false);
          return;
        }

        if (workspaceInfo.code === "organization_not_found") {
          setIsValidOrganization(false);
          setIsLoading(false);
          postLoading(false);
          return;
        }

        setIsLoading(false);
        dispatch(setNewWorkspace(true));
        postLoading(false);
        return;
      }

      setIsAuthenticated(true);
      dispatch(setToken(token, true));

      setIsValidOrganization(true);

      if (workspaceInfo.conflictResponse) {
        const { scope, conflictInfo } = workspaceInfo.conflictResponse;
        dispatch(updateChannelIdError({ conflict: { scope, conflictInfo } }));
      }

      if (channelId && !workspaceInfo.channelId) {
        workspaceInfo.channelId = channelId;
      }

      dispatch(setInfo(workspaceInfo));

      setIsConfigured(!workspaceInfo.isNew);

      // update knowledge base
      var knowledgeBase = workspaceInfo.knowledgeBase || { items: [] };
      const knowledgeBaseItems = [];

      for (const itemIndex in knowledgeBase.items) {
        const item = knowledgeBase.items[itemIndex];
        if (item.type === OPENING_HOURS_KEY) {
          knowledgeBaseItems.push({
            priority: 1,
            type: item.type,
            title: item.title,
            value: item.openingHours,
          });
        } else if (item.type === TEXT_KNOWLEDGE_ITEM_KEY) {
          knowledgeBaseItems.push({
            priority: 2,
            type: item.type,
            title: item.title,
            value: item.value,
          });
        }
      }

      knowledgeBase.knowledgeBaseItems = knowledgeBaseItems;
      knowledgeBase.config = getServiceConfig(KNOWLEDGE_BASE_KEY);

      dispatch(setKnowledgeBase(knowledgeBase));

      // update company directory
      const companyDirectory = workspaceInfo.companyDirectory || {};
      const existingDestinations = companyDirectory?.destinations
        ? companyDirectory.destinations
        : [];
      if (existingDestinations.length > 0) {
        dispatch(updateDestinations(existingDestinations));
      }

      companyDirectory.config = getServiceConfig(COMPANY_DIRECTORY_KEY);

      companyDirectory.supportedContactsInputMethods = getFeatureFlag(
        "supportedContactsInputMethods",
        null,
        companyDirectory.supportedContactsInputMethods || []
      );

      companyDirectory.contacts?.forEach((contact) => {
        contact.destinations = contact.destinations.map((destination) => {
          try {
            if (destination.type !== "phone_number") {
              return destination;
            }

            if (validatePhoneNumber(formatPhoneNumberUsingFormats(destination.value))) {
              return destination;
            }

            return {
              ...destination,
              type: "custom",
            };
          } catch (error) {
            return destination;
          }
        });
      });

      dispatch(setCompanyDirectory(companyDirectory));

      // update calendar management
      const calendarManagement = workspaceInfo.calendarManagement || {};

      if (workspaceInfo.isNew) {
        calendarManagement.isSameAsOpeningHours = true;
      }

      calendarManagement.config = getServiceConfig(CALENDAR_MANAGEMENT);
      dispatch(setCalendarManagement(calendarManagement));

      setIsLoading(false);
      postLoading(false);
      postHeight();
    };

    initialData();
  }, [
    isEnable,
    organizationId,
    accountId,
    workspaceId,
    token,
    channelId,
    accountName,
    workspaceName,
    agentId,
    agentName,
    dispatch,
  ]);

  useEffect(() => {
    // update currentPercentage when updatePercentage changes and make it smooth
    const interval = setInterval(() => {
      if (currentPercentage < updatePercentage) {
        setCurrentPercentage((prev) => prev + 1);
        postProgressData(currentPercentage + 1, currentMessage);
      }
    }, 50);
    return () => clearInterval(interval);
  }, [updatePercentage, currentPercentage]);

  const onSave = async () => {
    setCurrentMessage("");
    setCurrentPercentage(0);
    setUpdatePercentage(0);

    const { channelIdError } = store.getState().info;
    if (channelIdError?.conflict) {
      toast.error("Cannot save: Channel ID is already in use");
      scrollToFirstErrorElement("channel-id");
      return;
    }

    const validationResponse = await validateSave();
    if (!validationResponse.valid) {
      toast.error(validationResponse.message);
      scrollToFirstErrorElement(validationResponse.elementId);

      if (validationResponse.elementId === "channel-id") {
        dispatch(updateChannelIdError({ error: true }));
      }

      const errorType = validationResponse.type;
      if (invalidKnowledgeBaseKeys.includes(errorType)) {
        setTimeout(() => {
          dispatch(updateOpenKnowledgeBaseItems([validationResponse.accordionIndex]));
        }, 500);
      }

      if (invalidCompanyDirectoryKeys.includes(errorType)) {
        setTimeout(() => {
          dispatch(updateOpenContacts([validationResponse.accordionIndex]));
        }, 500);
      }

      if (invalidCalendarManagementKeys.includes(errorType)) {
        if (errorType === "invalid-appointment-type") {
          setTimeout(() => {
            dispatch(updateOpenAppointmentTypes([validationResponse.accordionIndex]));
          }, 500);
        }
      }

      return;
    }

    setIsSaving(true);
    postLoading(true);

    const saveObject = await createWorkspaceObject(organizationId, accountId, workspaceId);
    if (!saveObject) {
      postLoading(false);
      setIsSaving(false);
      return;
    }
    const saveResponse = await putRequest({
      endpoint: "snippet/info",
      data: saveObject,
      token: token,
    });

    if (saveResponse.ok) {
      const data = await saveResponse.json();
      if (data.error) {
        if (data.code === "channel_id_already_in_use") {
          const { accountName, workspaceName, agentName, agentId } = data;
          toast.error(
            <div style={{ minWidth: "320px", padding: "8px" }}>
              <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "12px" }}>
                {data.error}
              </div>
              <div style={{ fontSize: "14px", lineHeight: "1.6" }}>
                <div style={{ marginBottom: "6px" }}>
                  <span style={{ color: "#64748b", marginRight: "8px" }}>Account:</span>
                  <span style={{ fontWeight: "500" }}>{accountName || "N/A"}</span>
                </div>
                <div style={{ marginBottom: "6px" }}>
                  <span style={{ color: "#64748b", marginRight: "8px" }}>Workspace:</span>
                  <span style={{ fontWeight: "500" }}>{workspaceName || "N/A"}</span>
                </div>
                <div style={{ marginBottom: "6px" }}>
                  <span style={{ color: "#64748b", marginRight: "8px" }}>Agent:</span>
                  <span style={{ fontWeight: "500" }}>{agentName || "N/A"}</span>
                </div>
                <div>
                  <span style={{ color: "#64748b", marginRight: "8px" }}>Agent ID:</span>
                  <span style={{ fontWeight: "500", fontFamily: "monospace", fontSize: "12px" }}>
                    {agentId || "N/A"}
                  </span>
                </div>
              </div>
            </div>,
            { duration: 8000, style: { maxWidth: "420px" } }
          );
        } else {
          toast.error(data.error);
        }
        postLoading(false);
        setIsSaving(false);
        return;
      }

      postSubmitStatus("success");
      postProgressData(100, "Save completed");
      setCurrentPercentage(100);
      setUpdatePercentage(100);
      setIsSaving(false);
      setIsSaveProgressModalOpen(false);

      toast.success(`Workspace saved successfully!`);

      dispatch({ type: "SAVED" });
      postLoading(false);
    } else {
      const errorText = await saveResponse.text();
      toast.error(`Error saving workspace: ${errorText}`);
      postLoading(false);
    }
  };

  const onPrefill = useCallback(
    (eventData) => {
      const prefillData = eventData.prefillData;
      if (prefillData.contacts && prefillData.contacts.length > 0) {
        dispatch(updateContacts(prefillData.contacts));
      }

      if (prefillData.knowledgeBaseItems && prefillData.knowledgeBaseItems.length > 0) {
        const filteredKnowledgeItems = prefillData.knowledgeBaseItems.filter(
          (item) => item.type === OPENING_HOURS_KEY || item.type === TEXT_KNOWLEDGE_ITEM_KEY
        );
        dispatch(updateKnowledgeBaseItems(filteredKnowledgeItems));
      }

      if (prefillData.contactsMode) {
        dispatch(updateContactsMode(prefillData.contactsMode));
      }

      if (prefillData.netsapiensSyncDomain) {
        dispatch(updateSyncDomain(prefillData.netsapiensSyncDomain));
      }

      if (prefillData.preDefinedDestinations && Array.isArray(prefillData.preDefinedDestinations)) {
        const oldDestinations = destinations ? destinations : [];
        const usedDestinationsValues = updatedContacts
          .map((contact) =>
            contact.destinations
              .filter((destination) => destination.type === "extension")
              .map((destination) => destination.value)
          )
          .flat();
        const usedDestinations = oldDestinations.filter((destination) =>
          usedDestinationsValues.includes(destination.id)
        );
        const newDestinations = prefillData.preDefinedDestinations;
        if (newDestinations.length === 0) {
          dispatch(updateDestinations(usedDestinations));
          return;
        }

        const filteredDestinations = usedDestinations.filter(
          (destination) =>
            !newDestinations.some((newDestination) => newDestination.id === destination.id)
        );
        newDestinations.push(...filteredDestinations);
        dispatch(updateDestinations(newDestinations));
      }
    },
    [destinations, updatedContacts]
  );

  const onCloseSnippet = () => {
    if (!isValidToken) {
      postClose(true);
      return;
    }
    const hasChanges = getSaveButtonStatus();

    if (hasChanges) {
      if (externalOverlay) {
        const message = {
          childrenType: "exit",
          id: "exit-modal",
          headerTitle: "Leave page without saving?",
          bodyMessage: "You have unsaved changes. Are you sure you want to leave this page?",
          cancelText: "Cancel",
          exitWithoutSaving: "Exit without saving",
          exitWithSaving: "Save and exit",
        };

        postOpenModal(message);
      } else {
        setIsExitModalOpen(true);
      }
    } else {
      postClose(true);
    }
  };

  useEffect(() => {
    const handleParnetMessages = async (event) => {
      const eventData = event.data;
      if (eventData.type === "pre-fill") {
        onPrefill(eventData);
      }

      if (eventData.type === "save") {
        onSave();
      }

      if (eventData.type === "close") {
        onCloseSnippet();
      }
    };

    window.addEventListener("message", handleParnetMessages);

    return () => {
      window.removeEventListener("message", handleParnetMessages);
    };
  }, [isValidToken, destinations, updatedContacts]);

  useLayoutEffect(() => {
    const updateHeight = debounce(() => {
      postHeight();
    }, 100);

    updateHeight(); // Call once on mount
    window.addEventListener("resize", updateHeight); // Update on resize

    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  useEffect(() => {
    // postHeight();
    const handleResize = () => {
      const updateHeight = debounce(() => {
        postHeight();
      }, 100);

      updateHeight();
    };
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(document.body);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (isLoading) {
    return (
      <div className={` h-full flex flex-col overflow-x-hidden`}>
        <Toaster />
        <div className="h-[calc(100vh-10rem)] flex flex-col justify-center items-center">
          <Loader />
        </div>
      </div>
    );
  }

  if (!isValidOrganization) {
    return (
      <div className="flex p-4 flex-col space-y-4 ">
        <InvalidOrganization />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex p-4 flex-col space-y-4 ">
        <Unauthorized />
        <ExitModalContainer
          isExitModalOpen={isExitModalOpen}
          setIsExitModalOpen={setIsExitModalOpen}
          onSave={onSave}
        />
      </div>
    );
  }

  if (isEnable === false) {
    return (
      <div className="flex p-4 flex-col space-y-4 ">
        <DisabledWorkspace />
      </div>
    );
  }

  return (
    <div className="relative flex p-4 md:pb-4 flex-col space-y-6  overflow-hidden">
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            zIndex: 9999,
          },
        }}
      />
      <div className="absolute top-3 right-4">
        <ChannelSection canEditChannel={canEditChannel} />
      </div>
      <SaveErrorMessage />
      <YourReceptionist />
      <KnowledgeBase />
      <CompanyDirectory />
      <CalendarManagement />
      <WorkspaceActionButtons
        showActionButtons={showActionButtons}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
        onSave={onSave}
      />
      <SaveProgressModalContainer
        isSaveProgressModalOpen={isSaveProgressModalOpen}
        setIsSaveProgressModalOpen={setIsSaveProgressModalOpen}
        currentMessage={currentMessage}
        currentPercentage={currentPercentage}
      />
      <ExitModalContainer
        isExitModalOpen={isExitModalOpen}
        setIsExitModalOpen={setIsExitModalOpen}
        onSave={onSave}
      />
    </div>
  );
};

const SaveErrorMessage = () => {
  const dispatch = useDispatch();
  const { saveError } = useSelector((state) => state.info);

  const clearSaveError = () => {
    dispatch(setSaveError(null));
  };

  if (!saveError) {
    return null;
  }

  return (
    <div className="w-full bg-red-200 flex relative rounded-md p-2 items-center gap-4 shadow-lg">
      <div className="flex flex-col pr-10">
        <span className="text-red-700 font-semibold text-[10px] md:text-[14px]">Error</span>
        <span className="text-red-700 text-[10px] md:text-[14px]">{saveError}</span>
      </div>
      <img
        className="size-4 absolute right-4 cursor-pointer"
        src="/assets/icons/actions/x.svg"
        alt="Close"
        onClick={clearSaveError}
      />
    </div>
  );
};

export function Icon({ SvgComponent, color, height, width, className, ...props }) {
  return <SvgComponent fill={color || "currentColor"} className={className} {...props} />;
}

export default Workspace;
